// src/components/VaultUploader.js
// Direct file upload to Supabase Storage vault bucket
// Replaces URL-based system in admin Vault tab
import { useState, useRef } from 'react'
import { supabase, addVaultContent } from '../lib/supabase'
import './VaultUploader.css'

const MAX_MB = 50
const ALLOWED = ['image/jpeg','image/png','image/webp','image/gif','video/mp4','video/quicktime','video/webm']

export default function VaultUploader({ onUploaded, toast }) {
  const [form, setForm]       = useState({ title:'', description:'', minTier:'gold' })
  const [file, setFile]       = useState(null)
  const [preview, setPreview] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress]   = useState(0)
  const fileRef = useRef(null)

  function handleFile(e) {
    const f = e.target.files[0]
    if (!f) return
    if (!ALLOWED.includes(f.type)) {
      toast.show('Only images and videos are allowed', 'error'); return
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.show(`File must be under ${MAX_MB}MB`, 'error'); return
    }
    setFile(f)
    if (f.type.startsWith('image/')) {
      const r = new FileReader()
      r.onload = e => setPreview(e.target.result)
      r.readAsDataURL(f)
    } else {
      setPreview('video')
    }
  }

  function handleDrop(e) {
    e.preventDefault()
    const f = e.dataTransfer.files[0]
    if (f) handleFile({ target: { files: [f] } })
  }

  function clearFile() {
    setFile(null); setPreview(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  async function handleUpload(e) {
    e.preventDefault()
    if (!file) { toast.show('Select a file first', 'error'); return }
    if (!form.title.trim()) { toast.show('Enter a title', 'error'); return }

    setUploading(true); setProgress(15)

    try {
      const ext  = file.name.split('.').pop()
      const name = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`
      // Files live in a folder named after their required tier
      // (gold/premium/platinum) — the storage policy checks that
      // folder name against the viewer's own tier before allowing
      // access, so this placement is what actually enforces privacy.
      const path = `${form.minTier}/${name}`

      const { error: upErr } = await supabase.storage
        .from('vault')
        .upload(path, file, { cacheControl:'3600', upsert:false, contentType:file.type })

      if (upErr) throw upErr
      setProgress(70)

      // Store just the storage path — not a public URL. The bucket is
      // private; VaultGallery generates a short-lived signed URL only for
      // members whose tier actually unlocks this item.
      setProgress(85)

      const { error: dbErr } = await addVaultContent({
        title:       form.title.trim(),
        description: form.description.trim(),
        mediaUrl:    path,
        mediaType:   file.type.startsWith('video/') ? 'video' : 'photo',
        minTier:     form.minTier
      })
      if (dbErr) throw dbErr

      setProgress(100)
      toast.show('Added to vault!', 'success')
      setForm({ title:'', description:'', minTier:'gold' })
      clearFile()
      if (onUploaded) onUploaded()

    } catch (err) {
      toast.show(err.message || 'Upload failed', 'error')
    } finally {
      setUploading(false)
      setTimeout(() => setProgress(0), 800)
    }
  }

  return (
    <div className="vu">
      <form onSubmit={handleUpload}>

        {/* DROP ZONE */}
        <div
          className={`vu__drop ${file ? 'vu__drop--filled' : ''}`}
          onDragOver={e => e.preventDefault()}
          onDrop={handleDrop}
          onClick={() => !file && fileRef.current?.click()}
        >
          {!file ? (
            <>
              <div className="vu__drop-icon">↑</div>
              <p className="vu__drop-label">Tap or drag to upload</p>
              <p className="vu__drop-sub">Images & Videos · Max 50MB</p>
            </>
          ) : (
            <div className="vu__preview">
              {preview === 'video' ? (
                <div className="vu__video-thumb">
                  <div className="vu__play">▶</div>
                  <p className="vu__filename">{file.name}</p>
                </div>
              ) : (
                <img src={preview} alt="preview" className="vu__img" />
              )}
              <button type="button" className="vu__remove" onClick={e => { e.stopPropagation(); clearFile() }}>
                × Remove
              </button>
            </div>
          )}
        </div>

        <input ref={fileRef} type="file" accept="image/*,video/*" onChange={handleFile} style={{display:'none'}} />

        <div className="field" style={{marginTop:16}}>
          <label>Title</label>
          <input type="text" placeholder="e.g. Behind the scenes — LA shoot"
            value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} required />
        </div>

        <div className="field">
          <label>Description (optional)</label>
          <input type="text" placeholder="Brief caption..."
            value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} />
        </div>

        <div className="field">
          <label>Who can see this</label>
          <select value={form.minTier} onChange={e => setForm(f=>({...f,minTier:e.target.value}))}>
            <option value="gold">Gold — all members</option>
            <option value="premium">Premium and above</option>
            <option value="platinum">Platinum only</option>
          </select>
        </div>

        {uploading && (
          <div className="vu__progress">
            <div className="vu__progress-bar" style={{width:`${progress}%`}} />
            <span className="vu__progress-label">
              {progress < 50 ? 'Uploading...' : progress < 90 ? 'Processing...' : 'Almost done...'}
            </span>
          </div>
        )}

        <button className="btn btn--gold btn--full" type="submit" disabled={uploading || !file} style={{marginTop:8}}>
          {uploading ? `${progress}% — Please wait...` : 'Add to Vault'}
        </button>

      </form>
    </div>
  )
}
